import React, { useState } from 'react';
import { UserPlus, CalendarClock, TrendingUp, TrendingDown, Minus, Loader2, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';

const fmtDate = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '');

// How the linked maturity pillar has moved since this action was ticked off.
export function ActionImpact({ pr, testId }) {
  const im = pr?.impact;
  if (!im) return null;
  const up = im.delta > 0; const down = im.delta < 0;
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
  const tone = up ? 'bg-emerald-100 text-emerald-800' : down ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600';
  const label = im.reassessed ? `${Math.round(im.score_at)} → ${Math.round(im.score_now)} (${im.delta > 0 ? '+' : ''}${im.delta})` : `${Math.round(im.score_at)} at completion · no reassessment yet`;
  return <span className={cn('inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap', tone)} title={`${im.pillar_name}: pillar score when ticked vs latest assessment`} data-testid={testId} data-delta={im.delta}><Icon className="h-3 w-3" /> {im.pillar_name}: {label}</span>;
}

// Owner + due-date chip, with a leader-only assign popover.
export function ActionOwner({ pr, owners = [], canAssign, onAssign, testId }) {
  const [open, setOpen] = useState(false);
  const [owner, setOwner] = useState(pr?.owner_id || '');
  const [due, setDue] = useState(pr?.due_date || '');
  const [busy, setBusy] = useState(false);
  const overdue = !pr?.done && pr?.overdue;
  const chip = pr?.owner_name || pr?.due_date ? (
    <span className={cn('inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap', overdue ? 'bg-rose-100 text-rose-700 ring-1 ring-rose-200' : 'bg-indigo-50 text-indigo-700')} data-testid={`${testId}-chip`} data-overdue={overdue ? 'true' : 'false'}>
      <CalendarClock className="h-3 w-3" /> {pr.owner_name || 'Unassigned'}{pr.due_date ? ` · due ${fmtDate(pr.due_date)}` : ''}{overdue ? ` · ${Math.abs(pr.days_left)}d overdue` : ''}
    </span>
  ) : null;
  if (!canAssign) return chip;
  const save = async (clear = false) => {
    setBusy(true);
    try { await onAssign(clear ? { owner_id: null, due_date: null } : { owner_id: owner || null, due_date: due || null }); setOpen(false); } finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) { setOwner(pr?.owner_id || ''); setDue(pr?.due_date || ''); } }}>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 rounded-md hover:bg-slate-100 px-1 py-0.5" title="Assign owner and due date" data-testid={testId}>
          {chip || <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-400 hover:text-indigo-700"><UserPlus className="h-3 w-3" /> Assign</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3 space-y-2" onClick={(e) => e.stopPropagation()} data-testid={`${testId}-popover`}>
        <p className="text-xs font-semibold text-slate-700">Owner &amp; due date</p>
        <select value={owner} onChange={(e) => setOwner(e.target.value)} className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs" data-testid={`${testId}-owner-select`}>
          <option value="">— No owner —</option>
          {owners.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
        </select>
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs" data-testid={`${testId}-due-input`} />
        <p className="text-[10px] text-slate-500">The owner is notified now and nudged in-app each day the action is overdue.</p>
        <div className="flex items-center justify-between gap-2">
          <button type="button" onClick={() => save(true)} disabled={busy} className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-rose-600" data-testid={`${testId}-clear`}><X className="h-3 w-3" /> Clear</button>
          <button type="button" onClick={() => save(false)} disabled={busy} className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-indigo-700 disabled:opacity-60" data-testid={`${testId}-save`}>{busy && <Loader2 className="h-3 w-3 animate-spin" />} Save</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
